export type Unit =
  | 'Meters'
  | 'Feet'
  | 'Inches'
  | 'Millimeters'
  | 'FeetAndInches'

/**
 * Converts area from cm² to the target unit
 */
export const formatArea = (
  rawValue: number | undefined,
  currentUnit: Unit,
  locale: string,
  precision = 2
): string => {
  if (rawValue === undefined || rawValue === null) return 'N/A'
  if (isNaN(rawValue)) return 'N/A'

  let convertedValue: number
  let unitLabel: string

  switch (currentUnit) {
    case 'Meters':
      convertedValue = rawValue / 10000
      unitLabel = 'm²'
      break
    case 'Feet':
    case 'FeetAndInches':
      convertedValue = rawValue / 929.03
      unitLabel = 'ft²'
      break
    case 'Inches':
      convertedValue = rawValue / 6.4516
      unitLabel = 'in²'
      break
    case 'Millimeters':
      convertedValue = rawValue * 100
      unitLabel = 'mm²'
      break
    default:
      convertedValue = rawValue / 10000
      unitLabel = 'm²'
  }

  const formattedNumber = new Intl.NumberFormat(locale, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).format(convertedValue)

  return `${formattedNumber} ${unitLabel}`
}

export const convertFromCentimeters = (cm: number, unit: Unit): number => {
  switch (unit) {
    case 'Meters':
      return cm / 100
    case 'Feet':
    case 'FeetAndInches':
      return cm / 30.48
    case 'Inches':
      return cm / 2.54
    case 'Millimeters':
      return cm * 10
    default:
      return cm / 100
  }
}

export const convertToCentimeters = (value: number, unit: Unit): number => {
  switch (unit) {
    case 'Meters':
      return value * 100
    case 'Feet':
    case 'FeetAndInches':
      return value * 30.48
    case 'Inches':
      return value * 2.54
    case 'Millimeters':
      return value / 10
    default:
      return value * 100
  }
}

export const formatPerimeter = (
  rawValue: number | undefined,
  currentUnit: Unit,
  locale: string,
  precision = 2
): string => {
  if (rawValue === undefined || rawValue === null) return 'N/A'
  if (isNaN(rawValue)) return 'N/A'

  const convertedValue = convertFromCentimeters(rawValue, currentUnit)

  // FeetAndInches uses its own display format (e.g. "5 ft 3.2 in"); precision is not applicable.
  if (currentUnit === 'FeetAndInches') {
    return formatFeetAndInches(convertedValue, locale)
  }

  const unitLabel = getUnitLabel(currentUnit)

  const formattedNumber = new Intl.NumberFormat(locale, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).format(convertedValue)

  return `${formattedNumber} ${unitLabel}`
}

/**
 * Returns area in cm² by applying measurementScale to the raw Unreal-unit
 * value when available, falling back to the pre-scaled area field.
 */
export const scaleArea = (
  annotation: { areaUU?: number; area?: number },
  measurementScale: number
): number | undefined => {
  if (annotation.areaUU !== undefined)
    return annotation.areaUU * measurementScale * measurementScale
  return annotation.area
}

/**
 * Returns perimeter in cm by applying measurementScale to the raw Unreal-unit
 * value when available, falling back to the pre-scaled perimeter field.
 */
export const scalePerimeter = (
  annotation: { perimeterUU?: number; perimeter?: number },
  measurementScale: number
): number | undefined => {
  if (annotation.perimeterUU !== undefined)
    return annotation.perimeterUU * measurementScale
  return annotation.perimeter
}

export const scaleDepth = (
  annotation: { depth?: number },
  measurementScale: number
): number | undefined => {
  void measurementScale
  return annotation.depth
}

/**
 * Converts Unreal units to a formatted length string in the target display unit.
 *
 * Conversion: Unreal units × measurementScale → centimeters → display unit
 */
export const formatLengthFromUnrealUnits = (
  rawValueUnreal: number | undefined,
  measurementScale: number,
  currentUnit: Unit,
  locale: string,
  precision = 2
): string => {
  if (rawValueUnreal === undefined || rawValueUnreal === null) return 'N/A'
  if (isNaN(rawValueUnreal)) return 'N/A'

  const valueInCm = rawValueUnreal * measurementScale
  const convertedValue = convertFromCentimeters(valueInCm, currentUnit)

  // FeetAndInches uses its own display format (e.g. "5 ft 3.2 in"); precision is not applicable.
  if (currentUnit === 'FeetAndInches') {
    return formatFeetAndInches(convertedValue, locale)
  }

  const unitLabel = getUnitLabel(currentUnit)

  const formattedNumber = new Intl.NumberFormat(locale, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).format(convertedValue)

  return `${formattedNumber} ${unitLabel}`
}

export const formatFeetAndInches = (
  decimalFeet: number,
  locale: string
): string => {
  const totalInches = decimalFeet * 12
  let wholeFeet = Math.floor(decimalFeet)
  let remainingInches = Math.round((totalInches - wholeFeet * 12) * 10) / 10
  if (remainingInches >= 12) {
    wholeFeet += 1
    remainingInches = 0
  }
  const feetStr = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
  }).format(wholeFeet)
  const inchesStr = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(remainingInches)
  return `${feetStr} ft ${inchesStr} in`
}

export const getUnitLabel = (unit: Unit): string => {
  switch (unit) {
    case 'Meters':
      return 'm'
    case 'Feet':
      return 'ft'
    case 'FeetAndInches':
      return 'ft/in'
    case 'Inches':
      return 'in'
    case 'Millimeters':
      return 'mm'
    default:
      return 'm'
  }
}

/**
 * Converts volume from cm³ to the target unit
 */
export const formatVolume = (
  rawValue: number | undefined,
  currentUnit: Unit,
  locale: string,
  precision = 2
): string => {
  if (rawValue === undefined || rawValue === null) return 'N/A'
  if (isNaN(rawValue)) return 'N/A'

  let convertedValue: number
  let unitLabel: string

  switch (currentUnit) {
    case 'Meters':
      convertedValue = rawValue / 1000000
      unitLabel = 'm³'
      break
    case 'Feet':
    case 'FeetAndInches':
      convertedValue = rawValue / 28316.8
      unitLabel = 'ft³'
      break
    case 'Inches':
      convertedValue = rawValue / 16.387
      unitLabel = 'in³'
      break
    case 'Millimeters':
      convertedValue = rawValue * 1000
      unitLabel = 'mm³'
      break
    default:
      convertedValue = rawValue / 1000000
      unitLabel = 'm³'
  }

  const formattedNumber = new Intl.NumberFormat(locale, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).format(convertedValue)

  return `${formattedNumber} ${unitLabel}`
}
